import type { MessageKey, Translator } from "~/i18n";
import type { BillingInterval } from "../components/billing/PricingPanel";

const CATEGORY_KEYS: Readonly<Record<string, MessageKey>> = {
  "Models & AI": "pricing.category.modelsAi",
  "Agents & automation": "pricing.category.agentsAutomation",
  "Tools & connectors": "pricing.category.toolsConnectors",
  "Browser & compute": "pricing.category.browserCompute",
  "Storage & knowledge": "pricing.category.storageKnowledge",
  "Collaboration & org": "pricing.category.collaborationOrg",
  Other: "pricing.category.other",
};

const CAPABILITY_KEYS: Readonly<Partial<Record<string, MessageKey>>> = {
  "model.tier.pay": "pricing.cap.modelPay",
  "model.tier.super": "pricing.cap.modelSuper",
  "model.tier.max": "pricing.cap.modelMax",
  "agent.turns.monthly": "pricing.cap.agentTurns",
  "tool.calls.monthly": "pricing.cap.toolCalls",
  "subagents.concurrent": "pricing.cap.subagents",
  "connectors.count": "pricing.cap.connectors",
  "agents.max": "pricing.cap.agentsMax",
  "workflow.max": "pricing.cap.workflows",
  "routine.max": "pricing.cap.routines",
  "tools.catalog.full": "pricing.cap.fullToolLibrary",
  "connectors.custom": "pricing.cap.customConnectors",
  "agents.custom": "pricing.cap.customAgents",
  "browser.enabled": "pricing.cap.browser",
  "vault.storage.mb": "pricing.cap.vaultStorage",
  "memory.enabled": "pricing.cap.persistentMemory",
  "memory.storage.mb": "pricing.cap.memoryStorage",
  "knowledge.base": "pricing.cap.knowledgeBase",
  "org.memory": "pricing.cap.orgMemory",
  "org.roles": "pricing.cap.rolesPermissions",
  seats: "pricing.cap.seats",
};

const TAGLINE_KEYS: Readonly<Record<string, MessageKey>> = {
  free: "pricing.tagline.free",
  pay: "pricing.tagline.pay",
  super: "pricing.tagline.super",
  max: "pricing.tagline.max",
};

const INTERVAL_LABEL_KEYS: Readonly<Record<BillingInterval, MessageKey>> = {
  month: "pricing.interval.month",
  quarter: "pricing.interval.quarter",
  year: "pricing.interval.year",
};

const INTERVAL_BILLED_KEYS: Readonly<Record<BillingInterval, MessageKey>> = {
  month: "pricing.interval.billedMonthly",
  quarter: "pricing.interval.billedQuarterly",
  year: "pricing.interval.billedYearly",
};

export function localizedPricingCategory(category: string, t: Translator): string {
  const key = CATEGORY_KEYS[category];
  return key ? t(key) : category;
}

export function localizedCapabilityName(
  capability: string,
  fallback: string,
  t: Translator,
): string {
  const key = CAPABILITY_KEYS[capability];
  return key ? t(key) : fallback;
}

export function localizedPlanTagline(tier: string, t: Translator): string {
  const key = TAGLINE_KEYS[tier];
  return key ? t(key) : "";
}

export function localizedBillingInterval(
  interval: BillingInterval,
  t: Translator,
): { readonly label: string; readonly billedNote: string } {
  return {
    label: t(INTERVAL_LABEL_KEYS[interval]),
    billedNote: t(INTERVAL_BILLED_KEYS[interval]),
  };
}
